import { extend, cloneDeep } from 'lodash';

import { encodeText, decodeText } from '@/renderer/scripts/utils/tools/packages/encrypt';
import { TRANSFER_LIMIT, TransferSetting } from '@/renderer/scripts/utils/ConstantConfig';
import { StoreKey } from '@/renderer/scripts/utils/storage/StoreKey';

import EncryptedStore from './EncryptedStore';
import BrowserStore from './browserStore';

export * from './types';

interface StoreValue {
  key: StoreKey;
  type: 'keyStore' | 'configStore' | 'local' | 'session' | string; // 存储类型
  encrypt: boolean; // 是否加密
  defaultParams:
    | string
    | boolean
    | {
        [key: string]: any;
      };
}

const StoreKeyMap: Record<StoreKey, StoreValue> = {
  // 用户登录状态信息
  [StoreKey.USER_INFO]: {
    key: StoreKey.USER_INFO,
    type: 'keyStore',
    encrypt: true,
    defaultParams: {},
  },
  // 用户登录状态信息
  [StoreKey.TCD_USER_INFO]: {
    key: StoreKey.TCD_USER_INFO,
    type: 'keyStore',
    encrypt: true,
    defaultParams: {},
  },
  // 空间 token 信息
  [StoreKey.ACCESS_TOKEN_MAP]: {
    key: StoreKey.ACCESS_TOKEN_MAP,
    type: 'keyStore',
    encrypt: true,
    defaultParams: {},
  },
  // 分享外链的空间 token 信息
  [StoreKey.SHARE_ACCESS_TOKEN_MAP]: {
    key: StoreKey.SHARE_ACCESS_TOKEN_MAP,
    type: 'keyStore',
    encrypt: true,
    defaultParams: {},
  },
  // 文件收集的空间 token 信息
  [StoreKey.COLLECT_ACCESS_TOKEN_MAP]: {
    key: StoreKey.COLLECT_ACCESS_TOKEN_MAP,
    type: 'keyStore',
    encrypt: true,
    defaultParams: {},
  },
  // 文件同步的空间 token 信息
  [StoreKey.SYNC_ACCESS_TOKEN_MAP]: {
    key: StoreKey.SYNC_ACCESS_TOKEN_MAP,
    type: 'keyStore',
    encrypt: true,
    defaultParams: {},
  },
  // 窗口状态
  [StoreKey.WINDOW_STATE]: {
    key: StoreKey.WINDOW_STATE,
    type: 'configStore',
    encrypt: true,
    defaultParams: {
      isFullScreen: false,
      isMaximize: false,
      windowSizePreset: {
        minimum: {
          width: 1050,
          height: 700,
        },
        default: {
          width: 1440,
          height: 960,
        },
      },
    },
  },
  // 系统设置
  [StoreKey.SYSTEM_SETTING]: {
    key: StoreKey.SYSTEM_SETTING,
    type: 'configStore',
    encrypt: true,
    defaultParams: {
      autoStart: false,
      minToTray: true,
      autoUpdate: true,
      syncVirtualDisk: true,
    },
  },
  // 下载设置
  [StoreKey.DOWNLOAD_SETTING]: {
    key: StoreKey.DOWNLOAD_SETTING,
    type: 'configStore',
    encrypt: true,
    defaultParams: {
      downloadPath: '',
      noAskDownloadPath: false,
    },
  },
  [StoreKey.DOWNLOAD_TRANSFER_SETTING]: {
    key: StoreKey.DOWNLOAD_TRANSFER_SETTING,
    type: 'configStore',
    encrypt: false,
    defaultParams: {
      chunkParallelLimit: TRANSFER_LIMIT.download.chunkParallelLimit,
      batchLimit: TRANSFER_LIMIT.download.batchLimit,
      retryLimit: TRANSFER_LIMIT.download.retryLimit,
    },
  },
  [StoreKey.UPLOAD_TRANSFER_SETTING]: {
    key: StoreKey.UPLOAD_TRANSFER_SETTING,
    type: 'configStore',
    encrypt: false,
    defaultParams: {
      chunkParallelLimit: TRANSFER_LIMIT.download.chunkParallelLimit,
      batchLimit: TRANSFER_LIMIT.download.batchLimit,
      retryLimit: TRANSFER_LIMIT.download.retryLimit,
    },
  },
  // 路由信息
  [StoreKey.TCD_ROUTE]: {
    key: StoreKey.TCD_ROUTE,
    type: 'configStore',
    encrypt: false,
    defaultParams: { name: 'login' },
  },
  // 私有化服务器地址
  [StoreKey.INTERNAL_SMHURL]: {
    key: StoreKey.INTERNAL_SMHURL,
    type: 'configStore',
    encrypt: false,
    defaultParams: '',
  },

  // 登录状态信息
  [StoreKey.LOGIN]: {
    key: StoreKey.LOGIN,
    type: 'local',
    encrypt: false,
    defaultParams: { isLogined: false },
  },
  // 游客登录状态信息
  [StoreKey.VISTOR_LOGIN]: {
    key: StoreKey.VISTOR_LOGIN,
    type: 'local',
    encrypt: false,
    defaultParams: { isLogined: false },
  },
  // 路由信息
  [StoreKey.ROUTE]: {
    key: StoreKey.ROUTE,
    type: 'local',
    encrypt: false,
    defaultParams: {},
  },
  // 记住上一个路由信息
  [StoreKey.PREVIOUS_ROUTE]: {
    key: StoreKey.PREVIOUS_ROUTE,
    type: 'local',
    encrypt: false,
    defaultParams: { name: 'login' },
  },
  // 搜索历史记录
  [StoreKey.SEARCH_RECORD]: {
    key: StoreKey.SEARCH_RECORD,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // 搜索上一个路由信息
  [StoreKey.SEARCH_PREVIOUS_ROUTE]: {
    key: StoreKey.SEARCH_PREVIOUS_ROUTE,
    type: 'session',
    encrypt: false,
    defaultParams: { name: '' },
  },
  // 搜索范围 全局搜索 || 当前空间 || 当前文件夹
  [StoreKey.SEARCH_SCOPE]: {
    key: StoreKey.SEARCH_SCOPE,
    type: 'session',
    encrypt: false,
    defaultParams: 'all',
  },
  // 搜索关键词
  [StoreKey.SEARCH_KEYWORD]: {
    key: StoreKey.SEARCH_KEYWORD,
    type: 'session',
    encrypt: false,
    defaultParams: '',
  },
  // 搜索条件
  [StoreKey.SEARCH_CONDITION]: {
    key: StoreKey.SEARCH_CONDITION,
    type: 'session',
    encrypt: false,
    defaultParams: {},
  },
  // 团队空间选择的路径
  [StoreKey.TEAM_PATH]: {
    key: StoreKey.TEAM_PATH,
    type: 'local',
    encrypt: false,
    defaultParams: {},
  },
  // 进入收到的共享空间信息 spaceId, crumbStartIndex
  [StoreKey.RECV_SHARE_SPACE]: {
    key: StoreKey.RECV_SHARE_SPACE,
    type: 'session',
    encrypt: false,
    defaultParams: {},
  },
  // 进入回收站时选择的路径
  [StoreKey.RECYCLE_PATH]: {
    key: StoreKey.RECYCLE_PATH,
    type: 'session',
    encrypt: false,
    defaultParams: {},
  },
  // 进入分享 外链时初始路径
  [StoreKey.SHARE_LINK_ROOT_PATH]: {
    key: StoreKey.SHARE_LINK_ROOT_PATH,
    type: 'session',
    encrypt: false,
    defaultParams: [],
  },
  [StoreKey.MAJOR_VERSION]: {
    key: StoreKey.MAJOR_VERSION,
    type: 'local',
    encrypt: false,
    defaultParams: '0',
  },
  // WEB下传输列表打开上传的目录
  [StoreKey.REMOTE_PATH]: {
    key: StoreKey.REMOTE_PATH,
    type: 'session',
    encrypt: false,
    defaultParams: {},
  },
  // 预览前的路由信息
  [StoreKey.ROUTE_BEFORE_PREVIEW]: {
    key: StoreKey.ROUTE_BEFORE_PREVIEW,
    type: 'local',
    encrypt: false,
    defaultParams: {},
  },
  // 企业id操作记录
  [StoreKey.CUSTOM_ID_LIST]: {
    key: StoreKey.CUSTOM_ID_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // yufu sso 租户id操作记录
  [StoreKey.SSO_ID_LIST]: {
    key: StoreKey.SSO_ID_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // yufu sso 租户域名操作记录
  [StoreKey.SSO_DOMAIN_LIST]: {
    key: StoreKey.SSO_DOMAIN_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // yufu sso 企业id操作记录
  [StoreKey.SSO_CORPID_LIST]: {
    key: StoreKey.SSO_CORPID_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // sso 企业id操作记录
  [StoreKey.NEW_SSO_CORPID_LIST]: {
    key: StoreKey.NEW_SSO_CORPID_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // ldap 企业id操作记录
  [StoreKey.LDAP_CORPID_LIST]: {
    key: StoreKey.LDAP_CORPID_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // 企业id操作记录
  [StoreKey.ORG_INFO]: {
    key: StoreKey.ORG_INFO,
    type: 'local',
    encrypt: false,
    defaultParams: {},
  },
  // 侧边栏企业空间是否展开树形
  [StoreKey.OPEN_TEAM_TREE_IN_SIDEBAR]: {
    key: StoreKey.OPEN_TEAM_TREE_IN_SIDEBAR,
    type: 'local',
    encrypt: false,
    defaultParams: true,
  },
  // 展示顶部的广告
  [StoreKey.NOT_SHOW_TOP_ADVERT]: {
    key: StoreKey.NOT_SHOW_TOP_ADVERT,
    type: 'session',
    encrypt: false,
    defaultParams: [],
  },
  // 展示回收站的广告
  [StoreKey.NOT_SHOW_RECYCLE_ADVERT]: {
    key: StoreKey.NOT_SHOW_RECYCLE_ADVERT,
    type: 'local',
    encrypt: false,
    defaultParams: false,
  },
  // 展示侧边栏的广告
  [StoreKey.NOT_SHOW_BANNER_ADVERT]: {
    key: StoreKey.NOT_SHOW_BANNER_ADVERT,
    type: 'local',
    encrypt: false,
    defaultParams: false,
  },
  // 所在协作群组信息
  [StoreKey.COSPACE_SELECT_LIST]: {
    key: StoreKey.COSPACE_SELECT_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: [],
  },
  // 预览图片当前列表信息
  [StoreKey.IMG_PREVIEW_LIST]: {
    key: StoreKey.IMG_PREVIEW_LIST,
    type: 'local',
    encrypt: false,
    defaultParams: {},
  },
  // 微信登录用的码缓存
  [StoreKey.WECHAT_CODE_LIST]: {
    key: StoreKey.WECHAT_CODE_LIST,
    type: 'session',
    encrypt: false,
    defaultParams: [],
  },
  // 企微登录用的码缓存
  [StoreKey.WECOM_CODE_LIST]: {
    key: StoreKey.WECOM_CODE_LIST,
    type: 'session',
    encrypt: false,
    defaultParams: [],
  },
  // sso登录用的码缓存
  [StoreKey.SSO_CODE_LIST]: {
    key: StoreKey.SSO_CODE_LIST,
    type: 'session',
    encrypt: false,
    defaultParams: [],
  },
  // 是否收起协作空间的新手任务
  [StoreKey.PACK_UP_COLL_NOVICE_WORK]: {
    key: StoreKey.PACK_UP_COLL_NOVICE_WORK,
    type: 'session',
    encrypt: false,
    defaultParams: true,
  },
  // 是否收起个人空间的新手任务
  [StoreKey.PACK_UP_PERSONAL_NOVICE_WORK]: {
    key: StoreKey.PACK_UP_PERSONAL_NOVICE_WORK,
    type: 'session',
    encrypt: false,
    defaultParams: true,
  },

  // 安全设置
  [StoreKey.SECURITY_SETTING]: {
    key: StoreKey.SECURITY_SETTING,
    type: 'configStore',
    encrypt: false,
    defaultParams: {
      isbaitOpen: false,
      isAdmin: false,
    },
  },
  // 代理设置
  [StoreKey.PROXY_SETTING]: {
    key: StoreKey.PROXY_SETTING,
    type: 'configStore',
    encrypt: false,
    defaultParams: { proxy: 'no' },
  },
  // 语言设置
  [StoreKey.LANGUAGE_SETTING]: {
    key: StoreKey.LANGUAGE_SETTING,
    type: 'configStore',
    encrypt: false,
    defaultParams: { language: '' },
  },
  // 水印设置进行了修改（Y:表示当前被修改）
  [StoreKey.WATERMARK_FLAG]: {
    key: StoreKey.WATERMARK_FLAG,
    type: 'local',
    encrypt: false,
    defaultParams: '',
  },
  // cas 登录信息
  [StoreKey.CAS_LOGIN]: {
    key: StoreKey.CAS_LOGIN,
    type: 'local',
    encrypt: false,
    defaultParams: {},
  },
};

const storageMap: Record<string, EncryptedStore | BrowserStore> = {
  // 存储加密的 key
  keyStore: new EncryptedStore('keys'),
  // 存储配置
  configStore: new EncryptedStore('config'),
  local: new BrowserStore('local'),
  session: new BrowserStore('session'),
};

const storage = {
  set(key: StoreKey, value: unknown): void {
    const storeParams = StoreKeyMap[key];
    const storage = storageMap[storeParams.type];
    let val = value;
    if (storeParams.encrypt) {
      try {
        val = encodeText(JSON.stringify(value));
      } catch (error) {
        val = typeof storeParams.defaultParams === 'string' ? '' : null;
      }
    }
    storage.set(storeParams.key, val);
  },
  get<T>(key: StoreKey): T {
    const storeParams = StoreKeyMap[key];
    const storage = storageMap[storeParams.type];

    let value = storage.get(storeParams.key);
    if (storeParams.encrypt) {
      try {
        value = JSON.parse(decodeText(value as string));
      } catch (error) {
        //
        value = typeof storeParams.defaultParams === 'string' ? '' : null;
      }
    }

    if (typeof storeParams.defaultParams === 'string') {
      return (value || storeParams.defaultParams) as T;
    }
    if (typeof storeParams.defaultParams === 'boolean') {
      return (typeof value === 'boolean' ? value : storeParams.defaultParams) as unknown as T;
    }
    const defaultParams = cloneDeep(storeParams.defaultParams);
    const res = typeof defaultParams === 'object' ? extend<T>(defaultParams, value) : (defaultParams as unknown as T);
    return res;
  },
  del(key: StoreKey): void {
    const storeParams = StoreKeyMap[key];
    const storage = storageMap[storeParams.type];
    return storage.del(storeParams.key);
  },
  delOld(): void {
    /**
     * 2.3引入
     * 为了兼容曾经存在 localStorage 现在不需要该处存储才进行的删除。
     */
    localStorage.removeItem(storage.StoreKey.NOT_SHOW_TOP_ADVERT);
  },
  StoreKey,
};

storage.delOld();

export default storage;
